// Etsy Reviews + recovery tracking — EN + TR strings (shared seller + admin).

import type { LocalizedDict } from './index'

type ReviewsStrings = {
  sellerTitle: string
  adminTitle: string
  trackingTitle: string
  subtitle: string
  filters: {
    window: string
    windowOptions: { '10': string; '50': string; '100': string; all: string }
    rating: string
    allRatings: string
    status: string
    allStatuses: string
    search: string
    searchPlaceholder: string
    type: string
    allTypes: string
    internal: string
    external: string
    store: string
    allStores: string
    clear: string
  }
  columns: {
    rating: string
    review: string
    product: string
    order: string
    date: string
    recovery: string
    store: string
  }
  summary: {
    total: string
    sub5: string
    needsAttention: string
    inProgress: string
    resolved: string
  }
  sort: {
    sortBy: (column: string) => string
    ascending: string
    descending: string
  }
  inline: {
    startRecovery: string
    nextStep: string
    advanceTo: (status: string) => string
    working: string
  }
  notLinked: string
  noRecovery: string
  empty: string
  emptyFiltered: string
  loading: string
  loadError: string
  openInEtsy: string
  syncNow: string
  syncing: string
  lastSynced: (time: string) => string
  neverSynced: string
  syncCooldown: string
  syncStarting: string
  syncProgress: (done: number, total: number) => string
  syncCurrent: (store: string) => string
  syncFailedStores: (names: string) => string
  syncFinished: string
  syncFinishedWithFailures: (count: number) => string
  syncAlreadyRunning: string
  recoveryStatus: Record<
    | 'needs_outreach'
    | 'contacted'
    | 'offer_made'
    | 'resolved'
    | 'no_response'
    | 'declined',
    string
  >
  recovery: {
    panelTitle: string
    startButton: string
    startHint: string
    onlySubFive: string
    statusLabel: string
    offerLabel: string
    offerPlaceholder: string
    notesLabel: string
    notesPlaceholder: string
    transitionNote: string
    save: string
    saved: string
    saveError: string
    timeline: string
    started: string
    startError: string
    openedAt: string
  }
  drawer: {
    reviewText: string
    customer: string
    transaction: string
  }
  tracking: {
    intro: string
    internalGroup: string
    externalGroup: string
    enabled: string
    disabled: string
    syncError: string
    noStores: string
  }
}

export const reviewsI18n: LocalizedDict<ReviewsStrings> = {
  en: {
    sellerTitle: 'Reviews',
    adminTitle: 'All Reviews',
    trackingTitle: 'Review Tracking Settings',
    subtitle: 'Customer reviews and sub-5-star recovery tracking.',
    filters: {
      window: 'Window',
      windowOptions: { '10': 'Last 10', '50': 'Last 50', '100': 'Last 100', all: 'All' },
      rating: 'Rating',
      allRatings: 'All ratings',
      status: 'Recovery',
      allStatuses: 'All recovery',
      search: 'Search',
      searchPlaceholder: 'Search review text, product…',
      type: 'Type',
      allTypes: 'All types',
      internal: 'Internal',
      external: 'External',
      store: 'Store',
      allStores: 'All stores',
      clear: 'Clear filters',
    },
    columns: {
      rating: 'Rating',
      review: 'Review',
      product: 'Product',
      order: 'Order',
      date: 'Date',
      recovery: 'Recovery',
      store: 'Store',
    },
    summary: {
      total: 'Total',
      sub5: 'Sub-5★',
      needsAttention: 'Needs attention',
      inProgress: 'In progress',
      resolved: 'Resolved',
    },
    sort: {
      sortBy: (column) => `Sort by ${column}`,
      ascending: 'ascending',
      descending: 'descending',
    },
    inline: {
      startRecovery: 'Start recovery',
      nextStep: 'Next step',
      advanceTo: (status) => `Advance to ${status}`,
      working: 'Working…',
    },
    notLinked: 'Not linked',
    noRecovery: '—',
    empty: 'No reviews yet.',
    emptyFiltered: 'No reviews match your filters.',
    loading: 'Loading reviews…',
    loadError: 'Could not load reviews',
    openInEtsy: 'Open in Etsy',
    syncNow: 'Sync now',
    syncing: 'Syncing…',
    lastSynced: (time) => `Last synced ${time}`,
    neverSynced: 'Never synced',
    syncCooldown: 'Sync was run recently — please wait before syncing again.',
    syncStarting: 'Starting sync…',
    syncProgress: (done, total) => `Syncing — ${done} of ${total} stores done`,
    syncCurrent: (store) => `Now: ${store}`,
    syncFailedStores: (names) => `Could not sync: ${names}`,
    syncFinished: 'Reviews synced',
    syncFinishedWithFailures: (count) =>
      `Sync finished — ${count} ${count === 1 ? 'store' : 'stores'} could not be synced`,
    syncAlreadyRunning: 'A sync is already running — showing its progress.',
    recoveryStatus: {
      needs_outreach: 'Needs outreach',
      contacted: 'Contacted',
      offer_made: 'Offer made',
      resolved: 'Resolved',
      no_response: 'No response',
      declined: 'Declined',
    },
    recovery: {
      panelTitle: 'Recovery',
      startButton: 'Start recovery',
      startHint: 'Track outreach to recover this customer.',
      onlySubFive: 'Recovery is only available for sub-5-star reviews.',
      statusLabel: 'Status',
      offerLabel: 'Offer',
      offerPlaceholder: 'e.g. 20% off next order, free reship…',
      notesLabel: 'Notes',
      notesPlaceholder: 'Internal notes on this recovery…',
      transitionNote: 'Note for this status change (optional)',
      save: 'Save',
      saved: 'Recovery updated',
      saveError: 'Could not save recovery',
      timeline: 'Timeline',
      started: 'Recovery started',
      startError: 'Could not start recovery',
      openedAt: 'Opened',
    },
    drawer: {
      reviewText: 'Review',
      customer: 'Customer context',
      transaction: 'Transaction',
    },
    tracking: {
      intro:
        'Enable per-store review tracking. When on, sub-5-star reviews surface for recovery.',
      internalGroup: 'Internal stores (BO1)',
      externalGroup: 'External sellers',
      enabled: 'Enabled',
      disabled: 'Disabled',
      syncError: 'Sync failed',
      noStores: 'No stores to track.',
    },
  },
  tr: {
    sellerTitle: 'Yorumlar',
    adminTitle: 'Tüm Yorumlar',
    trackingTitle: 'Yorum Takip Ayarları',
    subtitle: 'Müşteri yorumları ve 5 yıldız altı kurtarma takibi.',
    filters: {
      window: 'Pencere',
      windowOptions: {
        '10': 'Son 10',
        '50': 'Son 50',
        '100': 'Son 100',
        all: 'Tümü',
      },
      rating: 'Puan',
      allRatings: 'Tüm puanlar',
      status: 'Kurtarma',
      allStatuses: 'Tüm kurtarma',
      search: 'Ara',
      searchPlaceholder: 'Yorum metni, ürün ara…',
      type: 'Tip',
      allTypes: 'Tüm tipler',
      internal: 'Dahili',
      external: 'Harici',
      store: 'Mağaza',
      allStores: 'Tüm mağazalar',
      clear: 'Filtreleri temizle',
    },
    columns: {
      rating: 'Puan',
      review: 'Yorum',
      product: 'Ürün',
      order: 'Sipariş',
      date: 'Tarih',
      recovery: 'Kurtarma',
      store: 'Mağaza',
    },
    summary: {
      total: 'Toplam',
      sub5: '5★ altı',
      needsAttention: 'İlgi gerekiyor',
      inProgress: 'Devam ediyor',
      resolved: 'Çözüldü',
    },
    sort: {
      sortBy: (column) => `${column} ölçütüne göre sırala`,
      ascending: 'artan',
      descending: 'azalan',
    },
    inline: {
      startRecovery: 'Kurtarma başlat',
      nextStep: 'Sonraki adım',
      advanceTo: (status) => `${status} durumuna geçir`,
      working: 'İşleniyor…',
    },
    notLinked: 'Bağlı değil',
    noRecovery: '—',
    empty: 'Henüz yorum yok.',
    emptyFiltered: 'Filtrelere uyan yorum yok.',
    loading: 'Yorumlar yükleniyor…',
    loadError: 'Yorumlar yüklenemedi',
    openInEtsy: "Etsy'de aç",
    syncNow: 'Şimdi senkronize et',
    syncing: 'Senkronize ediliyor…',
    lastSynced: (time) => `Son senkronizasyon ${time}`,
    neverSynced: 'Henüz senkronize edilmedi',
    syncCooldown: 'Senkronizasyon yakın zamanda çalıştırıldı — tekrar denemeden önce lütfen bekleyin.',
    syncStarting: 'Senkronizasyon başlıyor…',
    syncProgress: (done, total) => `Senkronize ediliyor — ${total} mağazadan ${done} tamam`,
    syncCurrent: (store) => `Şu an: ${store}`,
    syncFailedStores: (names) => `Senkronize edilemedi: ${names}`,
    syncFinished: 'Yorumlar senkronize edildi',
    syncFinishedWithFailures: (count) =>
      `Senkronizasyon bitti — ${count} mağaza senkronize edilemedi`,
    syncAlreadyRunning: 'Zaten süren bir senkronizasyon var — ilerlemesi gösteriliyor.',
    recoveryStatus: {
      needs_outreach: 'İletişim gerekiyor',
      contacted: 'İletişim kuruldu',
      offer_made: 'Teklif sunuldu',
      resolved: 'Çözüldü',
      no_response: 'Yanıt yok',
      declined: 'Reddedildi',
    },
    recovery: {
      panelTitle: 'Kurtarma',
      startButton: 'Kurtarma başlat',
      startHint: 'Bu müşteriyi kurtarmak için iletişimi takip et.',
      onlySubFive: 'Kurtarma yalnızca 5 yıldız altı yorumlarda kullanılabilir.',
      statusLabel: 'Durum',
      offerLabel: 'Teklif',
      offerPlaceholder: 'örn. sonraki siparişte %20 indirim, ücretsiz yeniden gönderim…',
      notesLabel: 'Notlar',
      notesPlaceholder: 'Bu kurtarmaya dair dahili notlar…',
      transitionNote: 'Bu durum değişikliği için not (isteğe bağlı)',
      save: 'Kaydet',
      saved: 'Kurtarma güncellendi',
      saveError: 'Kurtarma kaydedilemedi',
      timeline: 'Zaman çizelgesi',
      started: 'Kurtarma başlatıldı',
      startError: 'Kurtarma başlatılamadı',
      openedAt: 'Açılış',
    },
    drawer: {
      reviewText: 'Yorum',
      customer: 'Müşteri bağlamı',
      transaction: 'İşlem',
    },
    tracking: {
      intro:
        'Mağaza bazında yorum takibini aç. Açıkken 5 yıldız altı yorumlar kurtarma için listelenir.',
      internalGroup: 'Dahili mağazalar (BO1)',
      externalGroup: 'Harici satıcılar',
      enabled: 'Açık',
      disabled: 'Kapalı',
      syncError: 'Senkronizasyon başarısız',
      noStores: 'Takip edilecek mağaza yok.',
    },
  },
}
